import { useEffect, useState } from 'react';
import axios from 'axios';
import { Search, RefreshCw, X, Calendar, Skull, KeyRound, Radar, DatabaseZap, HelpCircle, ArrowRight, EyeOff, SlidersHorizontal, ChevronDown } from 'lucide-react';
import { useTenantScope } from '../context/TenantScopeContext';

export interface SecurityFilterBarItem {
  severity: string;
  mitre_tactic: string | null;
}

interface Props {
  search: string;
  onSearchChange: (v: string) => void;
  severities: Set<string>;
  onSeveritiesChange: (v: Set<string>) => void;
  statuses: Set<string>;
  onStatusesChange: (v: Set<string>) => void;
  fromDate: string;
  onFromDateChange: (v: string) => void;
  toDate: string;
  onToDateChange: (v: string) => void;
  hideNewAssets: boolean;
  onHideNewAssetsChange: (v: boolean) => void;
  hideEdrGap: boolean;
  onHideEdrGapChange: (v: boolean) => void;
  autoRefresh: number;
  onAutoRefreshChange: (v: number) => void;
  onRefresh: () => void;
  loading: boolean;
  items: SecurityFilterBarItem[];
}

const SEVERITY_OPTIONS: { value: string; label: string; activeClass: string; idleClass: string }[] = [
  { value: 'low', label: 'Low', activeClass: 'bg-gray-600 text-white ring-gray-600', idleClass: 'bg-gray-100 text-gray-600 hover:bg-gray-200' },
  { value: 'medium', label: 'Medium', activeClass: 'bg-amber-500 text-white ring-amber-500', idleClass: 'bg-amber-50 text-amber-700 hover:bg-amber-100' },
  { value: 'high', label: 'High', activeClass: 'bg-red-500 text-white ring-red-500', idleClass: 'bg-red-50 text-red-700 hover:bg-red-100' },
  { value: 'critical', label: 'Critical', activeClass: 'bg-red-800 text-white ring-red-800', idleClass: 'bg-red-100 text-red-800 hover:bg-red-200' },
];

const STATUS_OPTIONS: { value: string; label: string }[] = [
  { value: 'new', label: 'Pending' },
  { value: 'investigating', label: 'Fixing' },
  { value: 'contained', label: 'Contained' },
  { value: 'closed', label: 'Fixed' },
];

const AUTO_REFRESH_OPTIONS = [
  { value: 0, label: 'Auto-refresh: Off' },
  { value: 30, label: 'Auto-refresh: 30s' },
  { value: 60, label: 'Auto-refresh: 1 min' },
  { value: 300, label: 'Auto-refresh: 5 min' },
];

const SEVERITY_RANK: Record<string, number> = { low: 1, medium: 2, high: 3, critical: 4 };

// Raggruppa le tattiche MITRE ATT&CK (dati reali prodotti dal motore di detection) in macro
// categorie con colore/icona, per la sezione "Intelligent Assessment" della barra filtri.
const TACTIC_CATEGORIES: { name: string; color: string; icon: typeof Skull; tactics: string[] }[] = [
  { name: 'Active Exploitation', color: 'red', icon: Skull, tactics: ['Initial Access', 'Execution', 'Persistence', 'Privilege Escalation', 'Defense Evasion', 'Lateral Movement', 'Command and Control'] },
  { name: 'Identity Threats', color: 'orange', icon: KeyRound, tactics: ['Credential Access'] },
  { name: 'Reconnaissance & Scanning', color: 'amber', icon: Radar, tactics: ['Reconnaissance', 'Discovery'] },
  { name: 'Data & Impact Risks', color: 'purple', icon: DatabaseZap, tactics: ['Exfiltration', 'Impact', 'Collection'] },
];
const UNCATEGORIZED = { name: 'Other', color: 'gray', icon: HelpCircle };

const CATEGORY_HEADER_CLASS: Record<string, string> = {
  red: 'bg-red-500', orange: 'bg-orange-500', amber: 'bg-amber-500', purple: 'bg-purple-500', gray: 'bg-gray-400',
};
const CATEGORY_ICON_CLASS: Record<string, string> = {
  red: 'text-red-500', orange: 'text-orange-500', amber: 'text-amber-500', purple: 'text-purple-500', gray: 'text-gray-400',
};

// Periodo attivo in breve per il chip accanto a "Filters": "last 24h" quando l'inizio è a ~24 ore
// fa e manca la fine (il default delle pagine), altrimenti le date.
function periodLabel(fromDate: string, toDate: string): string {
  const fmt = (v: string) => new Date(v).toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
  if (!fromDate && !toDate) return 'all time';
  if (fromDate && !toDate) {
    const hours = Math.round((Date.now() - new Date(fromDate).getTime()) / 3_600_000);
    return hours > 0 && hours % 24 === 0 ? `last ${hours}h` : `since ${fmt(fromDate)}`;
  }
  return `${fromDate ? fmt(fromDate) : '…'} → ${toDate ? fmt(toDate) : 'now'}`;
}

function toggleInSet(set: Set<string>, value: string): Set<string> {
  const next = new Set(set);
  if (next.has(value)) next.delete(value); else next.add(value);
  return next;
}

// Barra filtri condivisa tra Security Incidents e Security Alerts: ricerca, chip di
// severità/stato, intervallo data/ora, auto-refresh, e un pannello "Intelligent Assessment"
// con il rapporto alert→incident e la ripartizione per categoria di minaccia (dati reali,
// calcolati sulle righe correntemente caricate/filtrate).
export default function SecurityFilterBar(props: Props) {
  const {
    search, onSearchChange, severities, onSeveritiesChange, statuses, onStatusesChange,
    fromDate, onFromDateChange, toDate, onToDateChange,
    hideNewAssets, onHideNewAssetsChange, hideEdrGap, onHideEdrGapChange, autoRefresh, onAutoRefreshChange,
    onRefresh, loading, items,
  } = props;
  const { selectedTenantId } = useTenantScope();
  const [showFilters, setShowFilters] = useState(false);
  const today = new Date();
  const activeFilterCount = severities.size + statuses.size + (hideNewAssets ? 1 : 0) + (hideEdrGap ? 1 : 0);
  const [stats, setStats] = useState<{ alertsCount: number; casesCount: number; ruleCounters: { ruleId: number; name: string; today: number }[] } | null>(null);

  useEffect(() => {
    axios.get('/api/stats', { params: { tenantId: selectedTenantId ?? undefined } }).then(res => setStats(res.data.data));
  }, [selectedTenantId]);

  const activeFilterChips: { key: string; label: string; onRemove: () => void }[] = [
    ...[...severities].map(s => ({ key: `sev-${s}`, label: `Severity: ${s}`, onRemove: () => onSeveritiesChange(toggleInSet(severities, s)) })),
    ...[...statuses].map(s => ({
      key: `status-${s}`,
      label: `Status: ${STATUS_OPTIONS.find(o => o.value === s)?.label}`,
      onRemove: () => onStatusesChange(toggleInSet(statuses, s)),
    })),
  ];

  function clearAllFilters() {
    onSearchChange(''); onSeveritiesChange(new Set()); onStatusesChange(new Set()); onFromDateChange(''); onToDateChange('');
  }

  const fusionPct = stats && stats.alertsCount > 0 ? Math.round((1 - stats.casesCount / stats.alertsCount) * 100) : null;

  const highCriticalShare = items.length > 0
    ? Math.round((items.filter(i => SEVERITY_RANK[i.severity] >= 3).length / items.length) * 100)
    : 0;

  const categoryCounts = TACTIC_CATEGORIES.map(cat => ({
    ...cat,
    count: items.filter(i => i.mitre_tactic && cat.tactics.includes(i.mitre_tactic)).length,
  }));
  const uncategorizedCount = items.filter(i => !i.mitre_tactic || !TACTIC_CATEGORIES.some(c => c.tactics.includes(i.mitre_tactic!))).length;

  return (
    <div className="mb-4">
      {/* Riga statistiche: rapporto alert -> incident (correlazione) + auto-refresh */}
      <div className="card p-3 mb-3 flex items-center justify-between bg-gray-900 text-white">
        <div className="flex items-center gap-3 text-sm">
          <span className="text-gray-300">Raw Alerts</span>
          <span className="font-semibold">{stats?.alertsCount ?? '—'}</span>
          <ArrowRight size={16} className="text-primary-400" />
          <span className="text-gray-300">Incidents</span>
          <span className="font-semibold">{stats?.casesCount ?? '—'}</span>
          {fusionPct !== null && fusionPct > 0 && (
            <span className="badge bg-primary-500 text-white">{fusionPct}% correlated</span>
          )}
          {stats?.ruleCounters.map(rc => (
            <span key={rc.ruleId} className="badge bg-gray-800 text-gray-300 inline-flex items-center gap-1"
              title={`"${rc.name}" is recorded as a running count today instead of one alert per occurrence — it's expected noise, not a detection.`}>
              <EyeOff size={11} /> {rc.today.toLocaleString('en-GB')} {rc.name.toLowerCase()} today (not alerted)
            </span>
          ))}
        </div>
        <div className="flex items-center gap-2">
          <select
            value={autoRefresh}
            onChange={e => onAutoRefreshChange(Number(e.target.value))}
            className="bg-gray-800 border border-gray-700 rounded-lg px-2 py-1.5 text-sm text-white"
          >
            {AUTO_REFRESH_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
          <button onClick={onRefresh} className="flex items-center gap-1 bg-gray-800 hover:bg-gray-700 rounded-lg px-3 py-1.5 text-sm">
            <RefreshCw size={14} className={loading ? 'animate-spin' : ''} /> Refresh
          </button>
        </div>
      </div>

      {/* Ricerca + chip severità/stato + intervallo data/ora */}
      <div className="card p-4 mb-3 bg-gradient-to-r from-primary-50 to-white border-primary-100">
        {/* Filtri a tendina (26/10/2026): resta in vista la ricerca, il resto si apre da
            "Filters"; accanto, la data di oggi e il periodo attivo, per sapere cosa si guarda. */}
        <div className="flex items-center gap-2">
          <div className="relative flex-1">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              value={search}
              onChange={e => onSearchChange(e.target.value)}
              placeholder="Search by host, user, IP or message…"
              className="input-field pl-9"
            />
          </div>
          <button onClick={() => setShowFilters(v => !v)}
            className={`flex items-center gap-1.5 px-3 py-2 rounded-lg border text-sm font-medium whitespace-nowrap ${
              showFilters ? 'bg-primary-600 border-primary-600 text-white' : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50'
            }`}>
            <SlidersHorizontal size={14} /> Filters{activeFilterCount > 0 && ` (${activeFilterCount})`}
            <ChevronDown size={14} className={`transition-transform ${showFilters ? 'rotate-180' : ''}`} />
          </button>
          <span className="flex items-center gap-1 px-2 py-1 rounded-md bg-white border border-gray-200 text-xs text-gray-600 whitespace-nowrap"
            title={`Today is ${today.toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}`}>
            <Calendar size={12} className="text-gray-400" />
            {today.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })}
            <span className="text-gray-400">· {periodLabel(fromDate, toDate)}</span>
          </span>
        </div>

        {showFilters && (
        <div className="flex flex-wrap items-center gap-4 mt-3 pt-3 border-t border-primary-100">
          <div className="flex items-center gap-1.5">
            <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide mr-1">Severity</span>
            {SEVERITY_OPTIONS.map(opt => {
              const active = severities.has(opt.value);
              return (
                <button
                  key={opt.value}
                  onClick={() => onSeveritiesChange(toggleInSet(severities, opt.value))}
                  className={`px-3 py-1 rounded-full text-xs font-medium transition-all duration-150 ${
                    active ? `${opt.activeClass} ring-2 ring-offset-1` : opt.idleClass
                  }`}
                >
                  {opt.label}
                </button>
              );
            })}
          </div>

          <div className="flex items-center gap-1.5">
            <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide mr-1">Fixing Status</span>
            {STATUS_OPTIONS.map(opt => {
              const active = statuses.has(opt.value);
              return (
                <button
                  key={opt.value}
                  onClick={() => onStatusesChange(toggleInSet(statuses, opt.value))}
                  className={`px-3 py-1 rounded-full text-xs font-medium transition-all duration-150 ${
                    active ? 'bg-primary-600 text-white ring-2 ring-primary-600 ring-offset-1' : 'bg-primary-50 text-primary-700 hover:bg-primary-100'
                  }`}
                >
                  {opt.label}
                </button>
              );
            })}
          </div>

          <div className="flex flex-col gap-1">
            <label className="flex items-center gap-1.5 text-xs text-gray-600 cursor-pointer select-none"
              title='Hides informational "New asset detected on network" items — one per device first seen, not a security detection.'>
              <input type="checkbox" checked={hideNewAssets} onChange={e => onHideNewAssetsChange(e.target.checked)} />
              Hide "New device detected"
            </label>
            <label className="flex items-center gap-1.5 text-xs text-gray-600 cursor-pointer select-none"
              title='Hides "Asset without EDR protection" items — a posture finding (one per unprotected device), not a security detection.'>
              <input type="checkbox" checked={hideEdrGap} onChange={e => onHideEdrGapChange(e.target.checked)} />
              Hide "Asset without EDR"
            </label>
          </div>

          <div className="flex items-center gap-1.5 ml-auto">
            <Calendar size={14} className="text-gray-400" />
            <input type="datetime-local" value={fromDate} onChange={e => onFromDateChange(e.target.value)} className="input-field w-auto py-1" />
            <span className="text-xs text-gray-400">to</span>
            <input type="datetime-local" value={toDate} onChange={e => onToDateChange(e.target.value)} className="input-field w-auto py-1" />
          </div>
        </div>
        )}

        {(activeFilterChips.length > 0 || fromDate || toDate || search) && (
          <div className="flex flex-wrap items-center gap-2 mt-3 pt-3 border-t border-primary-100">
            {activeFilterChips.map(chip => (
              <span key={chip.key} className="badge bg-primary-100 text-primary-800 flex items-center gap-1">
                {chip.label}
                <button onClick={chip.onRemove}><X size={12} /></button>
              </span>
            ))}
            <button onClick={clearAllFilters} className="text-xs text-gray-500 hover:text-gray-800 underline ml-1">
              Clear all
            </button>
          </div>
        )}
      </div>

      {/* Intelligent Assessment: quota high/critical sul totale filtrato + ripartizione per categoria di minaccia MITRE */}
      <div className="card p-4 flex flex-wrap gap-4">
        <div className="w-full md:w-48 shrink-0 border-r border-gray-100 pr-4">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Recommended Priority</p>
          <p className="text-xs text-gray-400 mb-2">Share of High/Critical in current view</p>
          <div className="h-2 rounded-full bg-gradient-to-r from-red-500 via-amber-400 to-gray-300 relative">
            <div
              className="absolute top-1/2 -translate-y-1/2 w-3 h-3 rounded-full bg-white border-2 border-gray-800 shadow"
              style={{ left: `calc(${100 - highCriticalShare}% - 6px)` }}
            />
          </div>
          <div className="flex justify-between text-[10px] text-gray-400 mt-1">
            <span>High</span><span>Low</span>
          </div>
        </div>

        {categoryCounts.map(cat => {
          const Icon = cat.icon;
          return (
            <div key={cat.name} className="flex-1 min-w-[140px]">
              <div className={`h-1 rounded-full mb-2 ${CATEGORY_HEADER_CLASS[cat.color]}`} />
              <div className="flex items-center gap-2">
                <span className={`text-xs font-medium ${CATEGORY_ICON_CLASS[cat.color]}`}>{cat.name}</span>
              </div>
              <div className="flex items-center gap-2 mt-1">
                <Icon size={22} className={CATEGORY_ICON_CLASS[cat.color]} />
                <span className="text-xl font-semibold text-gray-800">{cat.count}</span>
              </div>
            </div>
          );
        })}

        <div className="flex-1 min-w-[140px]">
          <div className={`h-1 rounded-full mb-2 ${CATEGORY_HEADER_CLASS[UNCATEGORIZED.color]}`} />
          <span className={`text-xs font-medium ${CATEGORY_ICON_CLASS[UNCATEGORIZED.color]}`}>{UNCATEGORIZED.name}</span>
          <div className="flex items-center gap-2 mt-1">
            <UNCATEGORIZED.icon size={22} className={CATEGORY_ICON_CLASS[UNCATEGORIZED.color]} />
            <span className="text-xl font-semibold text-gray-800">{uncategorizedCount}</span>
          </div>
        </div>
      </div>
    </div>
  );
}
